import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext(null);

// Feature access by tier
const TIER_FEATURES = {
  essentials: {
    scansPerMonth: 10,
    budgetEntries: 15,      // Budget/Project transaction limit
    splitEntries: 15,       // Split transaction limit
    hasTrips: true,         // Now available to all, just limited entries
    hasDeals: false,
    hasSmartAnalysis: false,
    hasPredictiveBudgeting: false,
    hasHiddenFeeDetection: false,
    hasPriorityProcessing: false,
  },
  prime: {
    scansPerMonth: 100,
    budgetEntries: 50,
    splitEntries: 50,
    hasTrips: true,
    hasDeals: true,
    hasSmartAnalysis: true,
    hasPredictiveBudgeting: false,
    hasHiddenFeeDetection: false,
    hasPriorityProcessing: true,
  },
  elite: {
    scansPerMonth: -1, // Unlimited
    budgetEntries: -1, // Unlimited
    splitEntries: -1,  // Unlimited
    hasTrips: true,
    hasDeals: true,
    hasSmartAnalysis: true,
    hasPredictiveBudgeting: true,
    hasHiddenFeeDetection: true,
    hasPriorityProcessing: true,
  }
};

export function SubscriptionProvider({ children }) {
  const { authFetch, isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false); // Don't block initial render
  const [previewTier, setPreviewTier] = useState(null);
  const [scanPackInfo, setScanPackInfo] = useState(null);
  const [transactionPackInfo, setTransactionPackInfo] = useState(null);
  const [powerPackInfo, setPowerPackInfo] = useState(null);
  const [entryUsage, setEntryUsage] = useState({
    budgetEntriesUsed: 0,
    budgetEntriesLimit: 15,
    splitEntriesUsed: 0,
    splitEntriesLimit: 15
  });

  // Check for admin preview mode
  useEffect(() => {
    const preview = localStorage.getItem('admin_preview_tier');
    if (preview && TIER_FEATURES[preview]) {
      setPreviewTier(preview);
    }
  }, []);

  const fetchSubscription = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    
    try {
      // Only fetch essential subscription status
      const response = await authFetch('/api/subscription/status');
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
        setEntryUsage({
          budgetEntriesUsed: data.budget_entries_used || 0,
          budgetEntriesLimit: data.budget_entries_limit || 15,
          splitEntriesUsed: data.split_entries_used || 0,
          splitEntriesLimit: data.split_entries_limit || 15
        });
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  }, [authFetch, isAuthenticated]);

  // Lazy load pack info only when needed
  const fetchPackInfo = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      // Fetch all pack info in parallel
      const [packRes, txPackRes, powerPackRes] = await Promise.all([
        authFetch('/api/subscription/scan-pack-info'),
        authFetch('/api/subscription/transaction-pack-info'),
        authFetch('/api/subscription/power-pack-info')
      ]);
      
      if (packRes.ok) setScanPackInfo(await packRes.json());
      if (txPackRes.ok) setTransactionPackInfo(await txPackRes.json());
      if (powerPackRes.ok) setPowerPackInfo(await powerPackRes.json());
    } catch (error) {
      console.error('Failed to fetch pack info:', error);
    }
  }, [authFetch, isAuthenticated]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Lazy load pack info after main subscription loads
  useEffect(() => {
    if (subscription && !scanPackInfo) {
      // Delay pack info fetch to not block initial load
      const timer = setTimeout(fetchPackInfo, 1000);
      return () => clearTimeout(timer);
    }
  }, [subscription, scanPackInfo, fetchPackInfo]);

  // Get current tier features (use preview tier if set by admin)
  const tier = previewTier || subscription?.tier || 'essentials';
  const features = TIER_FEATURES[tier] || TIER_FEATURES.essentials;
  const isPreviewMode = !!previewTier;
  const bonusScans = subscription?.bonus_scans || 0;

  // Check if user can use a scan (monthly + bonus scans)
  const canScan = () => {
    if (features.scansPerMonth === -1) return true; // Unlimited
    const totalAvailable = features.scansPerMonth + bonusScans;
    return (subscription?.scans_used || 0) < totalAvailable;
  };

  // Get remaining scans (monthly + bonus)
  const remainingScans = () => {
    if (features.scansPerMonth === -1) return Infinity;
    const totalAvailable = features.scansPerMonth + bonusScans;
    return Math.max(0, totalAvailable - (subscription?.scans_used || 0));
  };

  // Check feature access
  const hasFeature = (featureName) => {
    return features[featureName] || false;
  };

  // Check if user can add budget entry
  const canAddBudgetEntry = () => {
    if (features.budgetEntries === -1) return true; // Unlimited
    return entryUsage.budgetEntriesUsed < entryUsage.budgetEntriesLimit;
  };

  // Check if user can add split entry
  const canAddSplitEntry = () => {
    if (features.splitEntries === -1) return true; // Unlimited
    return entryUsage.splitEntriesUsed < entryUsage.splitEntriesLimit;
  };

  // Get remaining budget entries
  const remainingBudgetEntries = () => {
    if (features.budgetEntries === -1) return Infinity;
    return Math.max(0, entryUsage.budgetEntriesLimit - entryUsage.budgetEntriesUsed);
  };

  // Get remaining split entries
  const remainingSplitEntries = () => {
    if (features.splitEntries === -1) return Infinity;
    return Math.max(0, entryUsage.splitEntriesLimit - entryUsage.splitEntriesUsed);
  };

  // Use a budget entry (call after successful add)
  const useBudgetEntry = async () => {
    try {
      const response = await authFetch('/api/subscription/use-entry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entry_type: 'budget' })
      });
      if (response.ok) {
        const data = await response.json();
        setEntryUsage(prev => ({
          ...prev,
          budgetEntriesUsed: data.entries_used
        }));
        return data;
      }
    } catch (error) {
      console.error('Failed to record budget entry:', error);
    }
    return null;
  };

  // Use a split entry (call after successful add)
  const useSplitEntry = async () => {
    try {
      const response = await authFetch('/api/subscription/use-entry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entry_type: 'split' })
      });
      if (response.ok) {
        const data = await response.json();
        setEntryUsage(prev => ({
          ...prev,
          splitEntriesUsed: data.entries_used
        }));
        return data;
      }
    } catch (error) {
      console.error('Failed to record split entry:', error);
    }
    return null;
  };

  // Use a scan (call after successful scan)
  const useScan = async () => {
    try {
      const response = await authFetch('/api/subscription/use-scan', { method: 'POST' });
      if (response.ok) {
        const data = await response.json();
        setSubscription(prev => ({
          ...prev,
          scans_used: data.scans_used,
          bonus_scans: data.bonus_scans
        }));
        return data;
      }
    } catch (error) {
      console.error('Failed to record scan:', error);
    }
    return null;
  };

  // Purchase scan pack
  const purchaseScanPack = async (paymentMethod = 'stripe', packId = 'pack_15') => {
    try {
      if (paymentMethod === 'paypal') {
        const response = await authFetch('/api/subscription/paypal/checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            origin_url: window.location.origin,
            pack_type: 'scan_pack',
            pack_id: packId
          })
        });
        if (response.ok) {
          const data = await response.json();
          window.location.href = data.approval_url;
          return data;
        }
      } else {
        const response = await authFetch('/api/subscription/scan-pack/checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            origin_url: window.location.origin,
            payment_method: paymentMethod,
            pack_id: packId
          })
        });
        if (response.ok) {
          const data = await response.json();
          window.location.href = data.checkout_url;
          return data;
        }
      }
    } catch (error) {
      console.error('Failed to create scan pack checkout:', error);
    }
    return null;
  };

  // Purchase transaction pack
  const purchaseTransactionPack = async (paymentMethod = 'stripe', packId = 'tx_pack_15') => {
    try {
      if (paymentMethod === 'paypal') {
        const response = await authFetch('/api/subscription/paypal/checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            origin_url: window.location.origin,
            pack_type: 'transaction_pack',
            pack_id: packId
          })
        });
        if (response.ok) {
          const data = await response.json();
          window.location.href = data.approval_url;
          return data;
        }
      } else {
        const response = await authFetch('/api/subscription/transaction-pack/checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            origin_url: window.location.origin,
            payment_method: paymentMethod,
            pack_id: packId
          })
        });
        if (response.ok) {
          const data = await response.json();
          window.location.href = data.checkout_url;
          return data;
        }
      }
    } catch (error) {
      console.error('Failed to create transaction pack checkout:', error);
    }
    return null;
  };

  // Purchase power pack (combo)
  const purchasePowerPack = async (paymentMethod = 'stripe', packId = 'power_pro') => {
    try {
      if (paymentMethod === 'paypal') {
        const response = await authFetch('/api/subscription/paypal/checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            origin_url: window.location.origin,
            pack_type: 'power_pack',
            pack_id: packId
          })
        });
        if (response.ok) {
          const data = await response.json();
          window.location.href = data.approval_url;
          return data;
        }
      } else {
        const response = await authFetch('/api/subscription/power-pack/checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            origin_url: window.location.origin,
            payment_method: paymentMethod,
            pack_id: packId
          })
        });
        if (response.ok) {
          const data = await response.json();
          window.location.href = data.checkout_url;
          return data;
        }
      }
    } catch (error) {
      console.error('Failed to create power pack checkout:', error);
    }
    return null;
  };

  // Purchase subscription via PayPal
  const purchaseSubscriptionPayPal = async (tier) => {
    try {
      const response = await authFetch('/api/subscription/paypal/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          origin_url: window.location.origin,
          pack_type: 'subscription',
          pack_id: tier
        })
      });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.approval_url;
        return data;
      }
    } catch (error) {
      console.error('Failed to create PayPal subscription checkout:', error);
    }
    return null;
  };

  // Refresh subscription data
  const refresh = () => fetchSubscription();

  const bonusTransactions = subscription?.bonus_transactions || 0;

  const value = {
    subscription,
    subscriptionInfo: subscription, // Full subscription info including is_trial, trial_end
    loading,
    tier,
    tierName: subscription?.tier_name || 'Essentials',
    features,
    scansUsed: subscription?.scans_used || 0,
    scansLimit: features.scansPerMonth,
    bonusScans,
    totalScansAvailable: features.scansPerMonth === -1 ? Infinity : features.scansPerMonth + bonusScans,
    scanPackInfo,
    transactionPackInfo,
    powerPackInfo,
    bonusTransactions,
    canScan,
    remainingScans,
    hasFeature,
    useScan,
    purchaseScanPack,
    purchaseTransactionPack,
    purchasePowerPack,
    purchaseSubscriptionPayPal,
    refresh,
    refreshSubscription: refresh, // Alias for refresh
    isPremium: tier === 'prime' || tier === 'elite',
    isElite: tier === 'elite',
    isTrial: subscription?.is_trial || false,
    trialEnd: subscription?.trial_end || null,
    isPreviewMode,
    exitPreviewMode: () => {
      localStorage.removeItem('admin_preview_tier');
      setPreviewTier(null);
    },
    // Transaction limits
    entryUsage,
    canAddBudgetEntry,
    canAddSplitEntry,
    remainingBudgetEntries,
    remainingSplitEntries,
    useBudgetEntry,
    useSplitEntry
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {isPreviewMode && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-white text-center py-1 text-xs font-medium">
          Preview Mode: Viewing as {tier.charAt(0).toUpperCase() + tier.slice(1)} user
        </div>
      )}
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
